import { createElement, useCallback, useMemo, useState } from 'react';
import { map, range, pipe } from 'remeda';
import type { UseCalculateItemsProps } from './_props';

type MeasuredElement = {
  getBoundingClientRect: () => { height: number };
};

export default function useCalculateItems({
  containerHeight: height,
  containerWidth: width,
  topOffset,
  totalItemsCount,
  eachItemSize,
  paintedItemsAfterOverflow,
  children,
}: UseCalculateItemsProps) {
  const [scrollTop, setScrollTop] = useState(topOffset);
  const [measuredHeights, setMeasuredHeights] = useState<Record<number, number>>({});

  const itemLayouts = useMemo(() => {
    let offset = topOffset;

    return range(0, totalItemsCount).map((itemIndex) => {
      const itemHeight = measuredHeights[itemIndex] ?? eachItemSize;
      const layout = { height: itemHeight, top: offset };

      offset += itemHeight;

      return layout;
    });
  }, [totalItemsCount, measuredHeights, eachItemSize, topOffset]);

  const totalHeight = itemLayouts[totalItemsCount - 1]
    ? itemLayouts[totalItemsCount - 1].top + itemLayouts[totalItemsCount - 1].height
    : topOffset;

  const startIndex = Math.max(
    itemLayouts.findIndex(({ top, height: itemHeight }) => top + itemHeight >= scrollTop),
    0,
  );
  const visibleStopIndex = itemLayouts.findIndex(({ top }) => top > scrollTop + height);
  const endIndex = Math.min(
    (visibleStopIndex === -1 ? totalItemsCount : visibleStopIndex) + (paintedItemsAfterOverflow ?? 1),
    totalItemsCount,
  );

  const measureItem = useCallback((itemIndex: number, element: HTMLDivElement | null) => {
    if (!element) return;

    const measuredHeight = (element as unknown as MeasuredElement).getBoundingClientRect().height;

    setMeasuredHeights((currentMeasuredHeights) => {
      if (currentMeasuredHeights[itemIndex] === measuredHeight) return currentMeasuredHeights;

      return {
        ...currentMeasuredHeights,
        [itemIndex]: measuredHeight,
      };
    });
  }, []);

  const items = useMemo(
    () =>
      pipe(
        range(startIndex, endIndex),
        map((itemIndex) =>
          createElement(
            'div',
            {
              key: itemIndex,
              ref: (element: HTMLDivElement | null) => measureItem(itemIndex, element),
              style: {
                position: 'absolute',
                width,
                top: itemLayouts[itemIndex].top,
              },
            },
            children({
              index: itemIndex,
              style: { width },
            }),
          ),
        ),
      ),
    [startIndex, endIndex, measureItem, width, itemLayouts, children],
  );

  return {
    setScrollTop,
    totalHeight,
    items,
  };
}
